/* Immer relies on these 'dirty' tricks: https://redux-toolkit.js.org/api/createSlice#examples
 * note that "state" is already a draft of the original state and meant to be reassigned */
import { createSlice } from '@reduxjs/toolkit';
import {
  getCustomReportsForCampaign,
  getAllFilesByCampaignId,
  getProvenProcessInfo,
  getSelectedCampaignById,
  getSelectedCampaignUpdatesByCampaignId,
  createCustomReportForCampaign,
  deleteCustomReportForCampaign,
} from 'features/campaign/async';
import {
  DOCUMENT_FILE_TYPE_FILTER_LABELS,
  CampaignByIdState,
  DocumentFileTypeFilterLabel,
} from 'features/campaign/types';
import {
  fulfilled,
  initialRequestState,
  pending,
  rejected,
  requestReducer,
} from 'features/shared/request';
import { onFulfilledDo, onPending, onRejected } from 'libs/slice';

const initialState: CampaignByIdState = {
  selectedCampaign: {
    data: undefined,
    request: initialRequestState,
  },
  selectedCampaignUpdates: {
    data: undefined,
    request: initialRequestState,
  },
  customReports: {
    data: undefined,
    request: initialRequestState,
  },
  provenProcess: {
    data: undefined,
    request: initialRequestState,
  },
  documents: {
    data: undefined,
    request: initialRequestState,
  },
  ui: {
    searchForDocuments: undefined,
    fileTypeFilterLabel: DOCUMENT_FILE_TYPE_FILTER_LABELS.All,
  },
};

const slice = createSlice({
  name: 'campaignById',
  initialState,
  reducers: {
    setSelectedCampaign: (state, { payload }) => {
      state.selectedCampaign.data = payload;
      state.selectedCampaign.request = initialRequestState;
    },
    setSearchForDocuments(state, { payload }: { payload: string | undefined }) {
      state.ui.searchForDocuments = payload;
    },
    setFileTypeFilterForDocuments(
      state,
      { payload }: { payload: DocumentFileTypeFilterLabel }
    ) {
      state.ui.fileTypeFilterLabel = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getSelectedCampaignById.fulfilled,
      (state, { meta, payload }) => {
        if (state.selectedCampaign.request.id === meta.requestId) {
          state.selectedCampaign.data = payload;
          state.selectedCampaign.request = requestReducer(
            state.selectedCampaign.request,
            fulfilled({ meta })
          );
        }
      }
    );

    builder.addCase(getSelectedCampaignById.pending, (state, { meta }) => {
      state.selectedCampaign.request = requestReducer(
        state.selectedCampaign.request,
        pending({ meta })
      );
    });

    builder.addCase(getSelectedCampaignById.rejected, (state, { meta }) => {
      state.selectedCampaign.request = requestReducer(
        state.selectedCampaign.request,
        rejected({ meta })
      );
    });

    // Selected Campaign Updates

    builder.addCase(
      getSelectedCampaignUpdatesByCampaignId.fulfilled,
      (state, { meta, payload }) => {
        if (state.selectedCampaignUpdates.request.id === meta.requestId) {
          state.selectedCampaignUpdates.data = payload;
          state.selectedCampaignUpdates.request = requestReducer(
            state.selectedCampaignUpdates.request,
            fulfilled({ meta })
          );
        }
      }
    );

    builder.addCase(
      getSelectedCampaignUpdatesByCampaignId.pending,
      (state, { meta }) => {
        state.selectedCampaignUpdates.data = undefined;
        state.selectedCampaignUpdates.request = requestReducer(
          state.selectedCampaignUpdates.request,
          pending({ meta })
        );
      }
    );

    builder.addCase(
      getSelectedCampaignUpdatesByCampaignId.rejected,
      (state, { meta }) => {
        state.selectedCampaignUpdates.data = undefined;
        state.selectedCampaignUpdates.request = requestReducer(
          state.selectedCampaignUpdates.request,
          rejected({ meta })
        );
      }
    );

    // Custom Reports

    builder.addCase(
      getCustomReportsForCampaign.fulfilled,
      (state, { meta, payload }) => {
        if (state.customReports.request.id === meta.requestId) {
          state.customReports.data = payload;
          state.customReports.request = requestReducer(
            state.customReports.request,
            fulfilled({ meta })
          );
        }
      }
    );

    builder.addCase(getCustomReportsForCampaign.pending, (state, { meta }) => {
      state.customReports.request = requestReducer(
        state.customReports.request,
        pending({ meta })
      );
    });

    builder.addCase(getCustomReportsForCampaign.rejected, (state, { meta }) => {
      state.customReports.request = requestReducer(
        state.customReports.request,
        rejected({ meta })
      );
    });

    builder.addCase(
      createCustomReportForCampaign.fulfilled,
      (state, { meta, payload }) => {
        onFulfilledDo(
          state.customReports,
          meta,
          meta.requestId,
          payload,
          (_, state, payload) => {
            if (payload) {
              state.data = state.data ? [...state.data, payload] : [payload];
            }
          }
        );
      }
    );

    builder.addCase(
      createCustomReportForCampaign.pending,
      (state, { meta }) => {
        onPending(state.customReports, meta, false);
      }
    );

    builder.addCase(
      createCustomReportForCampaign.rejected,
      (state, { meta }) => {
        onRejected(state.customReports, meta, false);
      }
    );

    builder.addCase(
      deleteCustomReportForCampaign.fulfilled,
      (state, { meta, payload }) => {
        onFulfilledDo(
          state.customReports,
          meta,
          meta.requestId,
          payload,
          (meta, state) => {
            state.data = (state.data ?? []).filter((dto) => {
              return dto.id !== meta.arg.customReportId;
            });
          }
        );
      }
    );

    builder.addCase(
      deleteCustomReportForCampaign.pending,
      (state, { meta }) => {
        onPending(state.customReports, meta, false);
      }
    );

    builder.addCase(
      deleteCustomReportForCampaign.rejected,
      (state, { meta }) => {
        onRejected(state.customReports, meta, false);
      }
    );

    builder.addCase(
      getProvenProcessInfo.fulfilled,
      (state, { meta, payload }) => {
        if (state.provenProcess.request.id === meta.requestId) {
          state.provenProcess.data = payload;
          state.provenProcess.request = requestReducer(
            state.provenProcess.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getProvenProcessInfo.pending, (state, { meta }) => {
      state.provenProcess.data = undefined;
      state.provenProcess.request = requestReducer(
        state.provenProcess.request,
        pending({ meta })
      );
    });
    builder.addCase(getProvenProcessInfo.rejected, (state, { meta }) => {
      state.provenProcess.data = undefined;
      state.provenProcess.request = requestReducer(
        state.provenProcess.request,
        rejected({ meta })
      );
    });

    builder.addCase(
      getAllFilesByCampaignId.fulfilled,
      (state, { meta, payload }) => {
        if (state.documents.request.id === meta.requestId) {
          state.documents.data = payload;
          state.documents.request = requestReducer(
            state.documents.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getAllFilesByCampaignId.pending, (state, { meta }) => {
      state.documents.data = undefined;
      state.documents.request = requestReducer(
        state.documents.request,
        pending({ meta })
      );
    });
    builder.addCase(getAllFilesByCampaignId.rejected, (state, { meta }) => {
      state.documents.data = undefined;
      state.documents.request = requestReducer(
        state.documents.request,
        rejected({ meta })
      );
    });
  },
});

const { actions, reducer } = slice;

export const { setSearchForDocuments, setFileTypeFilterForDocuments } = actions;

export default reducer;
